// Offline-first queue for SOS status updates.
//
// Shape of each queue entry:
//   { id: uuid, text: string, user_id: string|null, timestamp: ISO,
//     origin: "safe" | "help" | "custom" }
//
// Persistence key: `pending_sos_queue` in localStorage.
// On `online` event (or manual call), POST /api/sos/sync with the batch and
// REMOVE only the IDs confirmed by the server. All network calls are gated by
// `navigator.onLine` — the queue itself works entirely offline.

import { api } from "./api";
import { safeGetItem, safeSetItem } from "./sosStorage";

const QUEUE_KEY = "pending_sos_queue";

function _uuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `sos_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function readQueue() {
  const raw = safeGetItem(QUEUE_KEY, "[]");
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (_e) {
    return [];
  }
}

function _writeQueue(items) {
  return safeSetItem(QUEUE_KEY, JSON.stringify(items));
}

/**
 * Append a new status entry to the queue. Returns the created entry.
 * Never throws; if persistence fails returns the entry in-memory anyway
 * so the caller can show it in the UI.
 */
export function enqueueStatus({ text, user_id = null, origin = "custom" }) {
  const entry = {
    id: _uuid(),
    text: String(text || "").slice(0, 500),
    user_id: user_id || null,
    timestamp: new Date().toISOString(),
    origin,
  };
  const current = readQueue();
  current.push(entry);
  _writeQueue(current);
  try {
    window.dispatchEvent(new CustomEvent("sos.queue.change", { detail: { size: current.length } }));
  } catch (_e) {
    /* ignore */
  }
  return entry;
}

export function clearQueue() {
  _writeQueue([]);
  try {
    window.dispatchEvent(new CustomEvent("sos.queue.change", { detail: { size: 0 } }));
  } catch (_e) {
    /* ignore */
  }
}

/**
 * Attempt to sync the full queue with the backend. Returns a summary
 * `{ attempted, confirmed, failed, offline }`. Safe to call repeatedly.
 * No-op if offline.
 */
export async function syncEmergencyQueue() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { attempted: 0, confirmed: 0, failed: 0, offline: true };
  }
  const items = readQueue();
  if (items.length === 0) {
    return { attempted: 0, confirmed: 0, failed: 0, offline: false };
  }
  try {
    const { data } = await api.post("/sos/sync", { updates: items });
    // Server returns {confirmed_ids: [...]} — remove only those from the queue.
    const confirmed = new Set(Array.isArray(data?.confirmed_ids) ? data.confirmed_ids : []);
    const remaining = items.filter((it) => !confirmed.has(it.id));
    _writeQueue(remaining);
    try {
      window.dispatchEvent(new CustomEvent("sos.queue.change", { detail: { size: remaining.length } }));
    } catch (_e) {
      /* ignore */
    }
    return {
      attempted: items.length,
      confirmed: confirmed.size,
      failed: items.length - confirmed.size,
      offline: false,
    };
  } catch (_err) {
    // Silent retry on next online event.
    return { attempted: items.length, confirmed: 0, failed: items.length, offline: false };
  }
}

/**
 * Attach a global `online` listener that syncs the queue when the browser
 * comes back online. Returns a cleanup function.
 */
export function installOnlineSyncListener(onResult) {
  const handler = async () => {
    const result = await syncEmergencyQueue();
    if (onResult) onResult(result);
  };
  window.addEventListener("online", handler);
  return () => window.removeEventListener("online", handler);
}
